import { Box, Typography } from '@mui/material'
import { useNavigate, useParams } from 'react-router-dom'
import { AppShell, Crumb } from '@agribot/polygon-checks-ui'
import { useAdminKeyUsers } from './hooks'
import { EmptyState, Eyebrow, Mono, StatusPill, Surface } from '@agribot/polygon-checks-ui'
import { SkeletonGridRows } from '@agribot/polygon-checks-ui'
import type { KeyUser } from './types'
import { formatDate, formatDateTime, ha, num } from '@agribot/polygon-checks-ui'
import { tokens } from '@agribot/polygon-checks-ui'

const COLUMNS = [
  { label: 'User', width: 'minmax(0,1.1fr)' },
  { label: 'Note', width: 'minmax(0,0.9fr)' },
  { label: 'Regions', width: '100px', align: 'right' as const },
  { label: 'Runs', width: '90px', align: 'right' as const },
  { label: 'Polygons', width: '110px', align: 'right' as const },
  { label: 'Area', width: '120px', align: 'right' as const },
  { label: 'Token', width: '90px', align: 'right' as const },
  { label: 'Last seen', width: '140px', align: 'right' as const },
]
const GRID = COLUMNS.map((c) => c.width).join(' ')

/**
 * What one key created.
 *
 * The question that matters when a key leaks. A provisioned user is a uuid
 * and nothing else — no name, no address, by design — so what is shown is
 * what they were granted and what they have done, read from the append-only
 * ledger rather than the live tables.
 */
export function KeyUsers() {
  const { keyId } = useParams<{ keyId: string }>()
  const navigate = useNavigate()
  const { data, isLoading } = useAdminKeyUsers(keyId)

  const key = data?.key
  const users = data?.users ?? []

  return (
    <AppShell
      crumbs={[
        <Crumb key="a">Admin</Crumb>,
        <Crumb key="b" to="/admin/keys">Keys</Crumb>,
        <Crumb key="c" muted>{key?.name ?? '…'}</Crumb>,
      ]}
    >
      <Box sx={{ flex: 1, overflowY: 'auto', px: 8, py: 7 }}>
        <Eyebrow sx={{ mb: 2 }}>Key</Eyebrow>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5, mb: 1 }}>
          <Typography variant="h1">{key?.name ?? ' '}</Typography>
          {key?.scope === 'platform:admin' && <StatusPill status="flagged" label="platform" />}
          {key?.revoked_at && <StatusPill status="rejected" label="revoked" />}
        </Box>
        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 5, maxWidth: 680 }}>
          {key?.organisation_name
            ? `${key.organisation_name} · `
            : 'No organisation · '}
          <Mono sx={{ fontSize: 12 }}>{key?.prefix}…</Mono>
          {key && ` · created ${formatDate(key.created_at)}`}
          {key?.last_used_at ? ` · last used ${formatDate(key.last_used_at)}` : ' · never used'}
        </Typography>

        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 5, maxWidth: 680 }}>
          Every user this key provisioned. Each is a uuid and nothing else —
          the partner holds the mapping to whoever is behind it, and we hold
          nothing that identifies anyone. Revoking the key above does not
          revoke these: their tokens are their own, so a leak means revoking
          each one deliberately.
        </Typography>

        <Surface sx={{ overflow: 'hidden' }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: GRID, columnGap: 3, px: 5 }}>
            {COLUMNS.map((c, i) => (
              <Box
                key={c.label || i}
                sx={{
                  display: 'flex', alignItems: 'center', height: 36,
                  justifyContent: c.align === 'right' ? 'flex-end' : 'flex-start',
                  font: `500 11px ${tokens.font.sans}`, letterSpacing: '0.08em',
                  textTransform: 'uppercase', color: 'text.disabled',
                }}
              >
                {c.label}
              </Box>
            ))}
            {isLoading && (
              <SkeletonGridRows
                columns={COLUMNS.length}
                rows={4}
                align={COLUMNS.map((c, i) => (c.align === 'right' ? i : -1)).filter((i) => i >= 0)}
              />
            )}
            {users.map((u) => <UserRow key={u.id} user={u} />)}
          </Box>

          {!isLoading && users.length === 0 && (
            <EmptyState
              title="This key has provisioned nobody"
              body={
                key?.scope === 'platform:admin'
                  ? 'A platform key mints other keys; it cannot create users at all.'
                  : 'Nothing has called POST /users with it yet.'
              }
            />
          )}
        </Surface>

        <Typography
          component="button"
          onClick={() => navigate('/admin/keys')}
          variant="body2"
          sx={{
            mt: 5, border: 0, background: 'none', cursor: 'pointer', p: 0,
            color: 'text.secondary', textAlign: 'left',
          }}
        >
          ← Back to keys
        </Typography>
      </Box>
    </AppShell>
  )
}

function UserRow({ user }: { user: KeyUser }) {
  const cell = {
    height: 48, display: 'flex', alignItems: 'center', minWidth: 0,
    borderTop: `1px solid ${tokens.color.ink200}`,
  }
  const right = (node: React.ReactNode) => (
    <Box sx={{ ...cell, justifyContent: 'flex-end', fontVariantNumeric: 'tabular-nums' }}>{node}</Box>
  )

  return (
    <Box sx={{ display: 'contents', '&:hover > *': { backgroundColor: tokens.color.ink50 } }}>
      <Box sx={{ ...cell, minWidth: 0 }}>
        <Box sx={{ minWidth: 0 }}>
          {/* The whole uuid: this is the reference a partner matches against
              their own records, so truncating it would defeat the purpose. */}
          <Mono sx={{ fontSize: 12 }}>{user.id}</Mono>
          <Typography variant="caption" sx={{ color: 'text.disabled', display: 'block' }}>
            created {formatDate(user.created_at)}
          </Typography>
        </Box>
      </Box>
      <Box sx={{ ...cell, minWidth: 0 }}>
        {/* Read-only here. The note is the partner's own record of who holds
            what; an operator reads it to make sense of a leak, and has no
            business rewriting it. */}
        <Typography
          variant="body2"
          sx={{ color: user.note ? 'text.secondary' : 'text.disabled' }}
          noWrap
          title={user.note ?? undefined}
        >
          {user.note ?? '—'}
        </Typography>
      </Box>
      {right(
        user.regions === 0
          ? <Typography variant="body2" sx={{ color: 'text.disabled' }}>none</Typography>
          : <Typography variant="body2">{num(user.regions)}</Typography>,
      )}
      {right(<Typography variant="body2">{num(user.runs)}</Typography>)}
      {right(<Typography variant="body2">{num(user.plots)}</Typography>)}
      {right(
        <Typography sx={{ font: `500 13px ${tokens.font.sans}` }}>{ha(user.area_ha, 0)}</Typography>,
      )}
      {right(
        // A version above 1 means somebody revoked this user's token at least
        // once, which is worth seeing next to what they have been doing.
        user.token_version > 1
          ? <StatusPill status="flagged" label={`v${user.token_version}`} />
          : <Typography variant="caption" sx={{ color: 'text.disabled' }}>original</Typography>,
      )}
      {right(
        <Typography variant="caption" sx={{ color: 'text.disabled' }}>
          {user.last_seen ? formatDateTime(user.last_seen) : 'never'}
        </Typography>,
      )}
    </Box>
  )
}
